// Strict HTML sanitizer for the employee-authored note descriptions we render with
// v-html. Notes carry a tiny formatting vocabulary (<b class="title">, <i>, <br>,
// <p>, ...); anything outside the allow-list — scripts, event handlers,
// <img onerror>, javascript: links — is stripped.
//
// Parsing happens inside a <template> element, whose content is an inert document
// fragment: scripts don't run and resources (img/src) aren't fetched while we clean.

// Tag -> allowed attributes. Only `class` is ever kept, and only on these tags.
const ALLOWED = {
  B: ['class'],
  STRONG: ['class'],
  I: ['class'],
  EM: ['class'],
  U: ['class'],
  P: ['class'],
  DIV: ['class'],
  SPAN: ['class'],
  BR: [],
  UL: ['class'],
  OL: ['class'],
  LI: ['class']
}

// Dropped entirely, contents and all — their text has no business on screen.
const DROP = new Set(['SCRIPT', 'STYLE', 'HEAD', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META'])

function clean (node) {
  // Snapshot: we mutate childNodes as we go.
  Array.from(node.childNodes).forEach(child => {
    if (child.nodeType === 1) { // element
      const tag = child.tagName
      if (DROP.has(tag)) {
        child.remove()
        return
      }
      const allowedAttrs = ALLOWED[tag]
      if (!allowedAttrs) {
        // Unknown-but-harmless wrapper: keep its (cleaned) contents, drop the tag.
        clean(child)
        child.replaceWith(...Array.from(child.childNodes))
        return
      }
      Array.from(child.attributes).forEach(attr => {
        if (!allowedAttrs.includes(attr.name.toLowerCase())) {
          child.removeAttribute(attr.name)
        }
      })
      clean(child)
    } else if (child.nodeType === 8) { // comment
      child.remove()
    }
  })
}

export function sanitizeHtml (html) {
  if (html == null) {
    return ''
  }
  const tpl = document.createElement('template')
  tpl.innerHTML = String(html)
  clean(tpl.content)
  return tpl.innerHTML
}

// For plain-text fields we render as HTML only to turn newlines into <br>. Escape
// first so a stray "<" in the text can never become markup, then apply nl2br.
export function textToHtml (text) {
  if (text == null) {
    return ''
  }
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
  return escaped.replace(/\n/g, '<br />')
}

export default sanitizeHtml
